/**
 * Sidebar State Utils
 * Utilities for persisting sidebar collapsed state and width to localStorage
 */

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
const SIDEBAR_WIDTH_KEY = 'sidebarWidth';

// Width constraints
export const SIDEBAR_MIN_WIDTH = 150;
export const SIDEBAR_MAX_WIDTH = 400;
export const SIDEBAR_DEFAULT_WIDTH = 240;
export const SIDEBAR_COLLAPSED_WIDTH = 60;

/**
 * Get the sidebar collapsed state from localStorage
 * @returns true if sidebar should be collapsed, false otherwise (default: false)
 */
export function getSidebarCollapsedState(): boolean {
  const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
  return stored === 'true';
}

/**
 * Set the sidebar collapsed state in localStorage
 * @param isCollapsed - true to save collapsed state, false for expanded
 */
export function setSidebarCollapsedState(isCollapsed: boolean): void {
  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
}

/**
 * Clamp a width value to the allowed range
 */
function clampWidth(width: number): number {
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, width));
}

/**
 * Get the sidebar width from localStorage
 * @returns The stored width clamped to min/max bounds, or default if not stored/invalid
 */
export function getSidebarWidth(): number {
  const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
  if (stored === null) {
    return SIDEBAR_DEFAULT_WIDTH;
  }
  const parsed = parseInt(stored, 10);
  if (isNaN(parsed)) {
    return SIDEBAR_DEFAULT_WIDTH;
  }
  return clampWidth(parsed);
}

/**
 * Set the sidebar width in localStorage
 * @param width - The width to store (will be clamped to min/max bounds)
 */
export function setSidebarWidth(width: number): void {
  const clamped = clampWidth(width);
  localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamped));
}
